// A WebSocketAdapter over a room Worker, so @esengine/rpc's RpcClient talks to the local room exactly as it
// would to a WebSocket server (docs/spikes/esengine-capability.md (c)).

import type { WebSocketAdapter } from '@esengine/rpc'
import { InvariantViolation } from '@taxwar/net'

/** WebSocket close codes (RFC 6455 §7.4.1). */
const CLOSE_NORMAL = 1000
const CLOSE_INTERNAL_ERROR = 1011

export class PostMessageSocket implements WebSocketAdapter {
  onopen: ((ev: Event) => void) | null = null
  onclose: ((ev: { code: number; reason: string }) => void) | null = null
  onerror: ((ev: Event) => void) | null = null
  onmessage: ((ev: { data: string | ArrayBuffer }) => void) | null = null

  private state: number = WebSocket.CONNECTING

  /** The socket owns the Worker: closing the socket terminates it. */
  constructor(private readonly worker: Worker) {
    worker.addEventListener('message', this.handleMessage)
    worker.addEventListener('error', this.handleError)
    worker.addEventListener('messageerror', this.handleMessageError)
    // Messages posted before the Worker script runs are queued by the browser, so the socket is open at once;
    // like a WebSocket, it reports that asynchronously, after the caller has attached its handlers.
    queueMicrotask(this.open)
  }

  get readyState(): number {
    return this.state
  }

  send(data: string | ArrayBuffer): void {
    if (this.state !== WebSocket.OPEN) throw new InvariantViolation('send on a room socket that is not open')
    if (typeof data === 'string') {
      this.worker.postMessage(data)
      return
    }
    // RpcClient passes the codec's Uint8Array (typed as ArrayBuffer), a view into the msgpack encoder's reusable
    // scratch buffer: transfer a copy, never the caller's bytes.
    const bytes = ArrayBuffer.isView(data) ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength) : new Uint8Array(data)
    const buffer = bytes.slice().buffer
    this.worker.postMessage(buffer, [buffer])
  }

  /** Idempotent, like WebSocket.close(); onclose fires asynchronously. */
  close(code: number = CLOSE_NORMAL, reason = ''): void {
    if (this.state === WebSocket.CLOSING || this.state === WebSocket.CLOSED) return
    this.state = WebSocket.CLOSING
    this.worker.removeEventListener('message', this.handleMessage)
    this.worker.removeEventListener('error', this.handleError)
    this.worker.removeEventListener('messageerror', this.handleMessageError)
    this.worker.terminate()
    queueMicrotask(() => {
      this.state = WebSocket.CLOSED
      this.onclose?.({ code, reason })
    })
  }

  private readonly open = (): void => {
    if (this.state !== WebSocket.CONNECTING) return
    this.state = WebSocket.OPEN
    this.onopen?.(new Event('open'))
  }

  private readonly handleMessage = (event: MessageEvent<unknown>): void => {
    const data = event.data
    if (typeof data !== 'string' && !(data instanceof ArrayBuffer)) throw new InvariantViolation('room posted a non-packet message')
    this.onmessage?.({ data })
  }

  // A room failure closes the socket with its reason instead of firing onerror: RpcClient would replace the
  // reason with a generic "WebSocket error", and the close alone already ends the connection.
  // A Worker whose script fails to load fires a plain Event. The room reports its own uncaught errors as a `fault`
  // message and cancels them, so an ErrorEvent here means the room failed before or while reporting.
  private readonly handleError = (event: Event): void => {
    const reason = event instanceof ErrorEvent ? `room worker error: ${event.message}` : 'room worker failed to load'
    this.close(CLOSE_INTERNAL_ERROR, reason)
  }

  private readonly handleMessageError = (): void => {
    this.close(CLOSE_INTERNAL_ERROR, 'room worker posted a message that could not be deserialized')
  }
}
